import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { paymentsApi, invoicesApi } from '../../services/api';
import toast from 'react-hot-toast';
import { HiOutlinePlus, HiOutlineCurrencyRupee, HiPencil, HiEye, HiX, HiTrash, HiOutlineDownload, HiOutlineSearch, HiOutlinePrinter, HiOutlineChatAlt2 } from 'react-icons/hi';
import PaymentForm from '../../components/Forms/PaymentForm';
import DeleteReasonModal from '../../components/DeleteReasonModal';
import SendWhatsAppModal from '../../components/WhatsApp/SendWhatsAppModal';
import TruncatedName from '../../components/TruncatedName';
import useDebouncedValue from '../../hooks/useDebouncedValue';
import { usePermissions } from '../../hooks/usePermissions';
import { formatCalendarDateDMY } from '../../utils/timezoneDisplay';
import { INLINE_FILTER_BAR, INLINE_FILTER_FIELD } from '../../utils/inlineListFilters';

const formatDate = formatCalendarDateDMY;

const isRowDeleted = (row) => Number(row?.is_deleted) === 1;

const Payments = () => {
  const { canCreate, canUpdate, canDelete } = usePermissions();
  const [searchParams] = useSearchParams();
  const focusId = searchParams.get('focusId');
  const deepSearch = searchParams.get('search');
  const [payments, setPayments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [detailPayment, setDetailPayment] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [paymentToDelete, setPaymentToDelete] = useState(null);
  const [deletingPayment, setDeletingPayment] = useState(false);
  const [showInvoiceDetail, setShowInvoiceDetail] = useState(false);
  const [detailInvoice, setDetailInvoice] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    payment_mode: '',
    date_from: '',
    date_to: '',
    page: 1
  });
  const [pagination, setPagination] = useState({});
  const [selectedInvoiceType, setSelectedInvoiceType] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [whatsappPayment, setWhatsappPayment] = useState(null);

  const debouncedSearch = useDebouncedValue(filters.search, 300);

  useEffect(() => {
    if (deepSearch) {
      setFilters((prev) => ({ ...prev, search: deepSearch, page: 1 }));
    }
  }, [deepSearch]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, filters.payment_mode, filters.date_from, filters.date_to, filters.page, selectedInvoiceType, showDeleted]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
        ...(filters.payment_mode ? { payment_mode: filters.payment_mode } : {}),
        ...(filters.date_from ? { date_from: filters.date_from } : {}),
        ...(filters.date_to ? { date_to: filters.date_to } : {}),
        ...(selectedInvoiceType ? { invoice_type: selectedInvoiceType } : {}),
        ...(showDeleted ? { include_deleted: 1 } : {}),
        page: filters.page,
        limit: 20
      };
      const [paymentsRes, statsRes] = await Promise.all([
        paymentsApi.getAll(params),
        paymentsApi.getStats()
      ]);
      setPayments(paymentsRes.data.data.payments || []);
      setPagination(paymentsRes.data.data.pagination || {});
      setStats(statsRes.data.data);
    } catch (error) {
      toast.error('Failed to fetch payments');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (name, value) => {
    setFilters({ ...filters, [name]: value, page: 1 });
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      payment_mode: '',
      date_from: '',
      date_to: '',
      page: 1,
    });
    setSelectedInvoiceType('');
  };

  const hasActiveFilters =
    filters.search ||
    filters.payment_mode ||
    filters.date_from ||
    filters.date_to ||
    selectedInvoiceType;

  const handleCreatePayment = () => {
    setSelectedInvoice(null);
    setSelectedInvoiceType(''); // Reset invoice type selection
    setShowForm(true);
  };

  const handleFormSuccess = () => {
    fetchData();
  };

  const handleView = async (payment) => {
    try {
      const response = await paymentsApi.getById(payment.id);
      setDetailPayment(response.data.data);
      setShowDetail(true);
    } catch (error) {
      toast.error('Failed to load payment details');
    }
  };

  const handleEdit = async (payment) => {
    try {
      const response = await paymentsApi.getById(payment.id);
      setSelectedPayment(response.data.data);
      setShowForm(true);
    } catch (error) {
      toast.error('Failed to load payment details');
    }
  };

  const handleDelete = (payment) => {
    setPaymentToDelete(payment);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async (deleteReason) => {
    if (!paymentToDelete) return;

    setDeletingPayment(true);
    try {
      await paymentsApi.delete(paymentToDelete.id, { delete_reason: deleteReason });
      toast.success('Payment deleted successfully');
      setShowDeleteConfirm(false);
      setPaymentToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete payment');
    } finally {
      setDeletingPayment(false);
    }
  };

  const handleInvoiceClick = async (invoiceId) => {
    try {
      const response = await invoicesApi.getById(invoiceId);
      setDetailInvoice(response.data.data);
      setShowInvoiceDetail(true);
    } catch (error) {
      toast.error('Failed to load invoice details');
    }
  };

  const handleWhatsAppPayment = (payment) => {
    setWhatsappPayment(payment);
    setShowWhatsAppModal(true);
  };

  const previewPaymentWhatsApp = async () => {
    const res = await paymentsApi.previewWhatsAppSend(whatsappPayment.id);
    return res.data.data;
  };

  const sendPaymentWhatsApp = async (data) => {
    await paymentsApi.sendWhatsApp(whatsappPayment.id, data);
    fetchData();
  };

  const readPdfErrorMessage = async (error, fallback) => {
    const data = error.response?.data;
    if (data instanceof Blob) {
      try {
        const t = await data.text();
        const j = JSON.parse(t);
        if (j.message) return j.message;
      } catch {
        /* ignore */
      }
      return fallback;
    }
    if (typeof data?.message === 'string') return data.message;
    return fallback;
  };

  const chooseRenderStyle = () => {
    const ok = window.confirm(
      'Choose template:\n\nOK = Image template (Header + Footer)\nCancel = Default template'
    );
    return ok ? 'letterhead' : 'default';
  };

  const buildPaymentPdfOverrides = (payment, render_style) => {
    const overrides = { render_style, document_type: 'payment_receipt' };
    if (payment?.payment_mode) overrides.payment_mode = payment.payment_mode;
    if (payment?.receipt_number) overrides.display_number = payment.receipt_number;
    if (payment?.payment_date) overrides.payment_date = payment.payment_date;
    if (payment?.id) overrides.payment_id = payment.id;
    return overrides;
  };

  const buildPaymentDocxParams = (payment) => ({
    display_number: payment?.receipt_number || undefined,
    payment_date: payment?.payment_date || undefined,
    payment_id: payment?.id || undefined,
  });

  const invoiceDownloadLabel = (payment) =>
    payment?.receipt_number || payment?.invoice_number || payment?.invoice_id;

  const parseTemplateExportResponse = (response, defaultBaseLabel) => {
    const serverMime = response.headers?.['content-type'] || 'application/pdf';
    const isPdf = /pdf/i.test(serverMime);
    const blob = new Blob([response.data], { type: serverMime });
    const ext = isPdf ? 'pdf' : 'docx';
    let filename = `Invoice_${defaultBaseLabel}.${ext}`;
    const cd = response.headers?.['content-disposition'];
    if (cd) {
      const m = /filename="?([^"]+)"?/i.exec(cd);
      if (m && m[1]) filename = m[1];
    }
    const fallbackReason = response.headers?.['x-pdf-fallback-reason'];
    return { blob, filename, isPdf, fallbackReason };
  };

  const triggerBlobDownload = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const toastTemplateExportOutcome = (isPdf, render_style, fallbackReason) => {
    if (render_style === 'letterhead' && !isPdf) {
      toast(
        'Image template works only on PDF. This download is DOCX because PDF conversion failed on the server.',
        { icon: 'ℹ️', duration: 7000 }
      );
    } else if (fallbackReason === 'libreoffice-not-installed') {
      toast('Downloaded as DOCX — install LibreOffice on the server to enable PDF export.', { icon: 'ℹ️', duration: 6000 });
    } else if (fallbackReason) {
      toast('Downloaded as DOCX — PDF conversion failed.', { icon: 'ℹ️', duration: 5000 });
    } else {
      toast.success(isPdf ? 'Invoice downloaded successfully' : 'DOCX downloaded successfully');
    }
  };

  const downloadInvoicePdf = async (payment) => {
    try {
      const render_style = chooseRenderStyle();
      const overrides = buildPaymentPdfOverrides(payment, render_style);

      const isTemplate = !!payment?.template_code;
      const invoiceId = payment?.invoice_id;
      const label = invoiceDownloadLabel(payment);

      if (isTemplate) {
        const response = await invoicesApi.getDocx(
          invoiceId,
          'pdf',
          render_style,
          buildPaymentDocxParams(payment)
        );
        const { blob, filename, isPdf, fallbackReason } = parseTemplateExportResponse(response, label);
        triggerBlobDownload(blob, filename);
        await invoicesApi.logDownload({
          invoice_id: invoiceId,
          download_type: isPdf ? 'pdf' : 'docx',
          notes_override: null
        });
        toastTemplateExportOutcome(isPdf, render_style, fallbackReason);
        return;
      }

      const response = await invoicesApi.getPdf(invoiceId, null, overrides);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const prefix = payment?.receipt_number ? 'PaymentReceipt' : 'Invoice';
      triggerBlobDownload(blob, `${prefix}_${label}.pdf`);
      await invoicesApi.logDownload({
        invoice_id: invoiceId,
        download_type: 'pdf',
        notes_override: null
      });
      toast.success('Invoice downloaded successfully');
    } catch (error) {
      toast.error(await readPdfErrorMessage(error, 'Failed to download invoice'));
    }
  };

  const printInvoicePdf = async (payment) => {
    try {
      const render_style = chooseRenderStyle();
      const invoiceId = payment?.invoice_id;
      const isTemplate = !!payment?.template_code;
      const label = invoiceDownloadLabel(payment);

      if (isTemplate) {
        const response = await invoicesApi.getDocx(
          invoiceId,
          'pdf',
          render_style,
          buildPaymentDocxParams(payment)
        );
        const { blob, filename, isPdf, fallbackReason } = parseTemplateExportResponse(response, label);
        if (!isPdf) {
          triggerBlobDownload(blob, filename);
          await invoicesApi.logDownload({
            invoice_id: invoiceId,
            download_type: 'docx',
            notes_override: `Print from Payments (DOCX; receipt ${label})`
          });
          toastTemplateExportOutcome(isPdf, render_style, fallbackReason);
          return;
        }
        const url = window.URL.createObjectURL(blob);
        const w = window.open(url, '_blank', 'noopener,noreferrer');
        if (!w) {
          window.URL.revokeObjectURL(url);
          toast.error('Popup blocked. Please allow popups to print.');
          return;
        }
        const cleanup = () => {
          try { window.URL.revokeObjectURL(url); } catch { /* ignore */ }
        };
        w.addEventListener('beforeunload', cleanup, { once: true });
        w.addEventListener('load', () => {
          try {
            w.focus();
            w.print();
          } catch {
            /* ignore */
          }
        });
        await invoicesApi.logDownload({
          invoice_id: invoiceId,
          download_type: 'print',
          notes_override: `Print from Payments (${payment?.receipt_number || payment?.invoice_number || ''})`
        });
        return;
      }

      const response = await invoicesApi.getPdf(invoiceId, null, buildPaymentPdfOverrides(payment, render_style));
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      const w = window.open(url, '_blank', 'noopener,noreferrer');
      if (!w) {
        window.URL.revokeObjectURL(url);
        toast.error('Popup blocked. Please allow popups to print.');
        return;
      }

      const cleanup = () => {
        try { window.URL.revokeObjectURL(url); } catch { /* ignore */ }
      };

      w.addEventListener('beforeunload', cleanup, { once: true });
      w.addEventListener('load', () => {
        try {
          w.focus();
          w.print();
        } catch {
          /* ignore */
        }
      });

      await invoicesApi.logDownload({
        invoice_id: invoiceId,
        download_type: 'print',
        notes_override: `Print from Payments (${payment?.receipt_number || payment?.invoice_number || ''})`
      });
    } catch (error) {
      toast.error(await readPdfErrorMessage(error, 'Failed to print invoice'));
    }
  };

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-gray-900">Payments</h1>
          <p className="text-gray-500 mt-1">Track and manage payment collections</p>
        </div>
        {canCreate('payments') && (
          <button type="button" onClick={handleCreatePayment} className="btn btn-primary shrink-0">
            <HiOutlinePlus className="mr-2 h-5 w-5" />
            Payment
          </button>
        )}
      </div>

      <div className={INLINE_FILTER_BAR}>
        <div className="relative min-w-[9rem] flex-1 max-w-[14rem]">
          <HiOutlineSearch className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })}
            placeholder="Search…"
            className={`${INLINE_FILTER_FIELD} w-full pl-8`}
          />
        </div>
        <select
          value={selectedInvoiceType}
          onChange={(e) => {
            setSelectedInvoiceType(e.target.value);
            setFilters((prev) => ({ ...prev, page: 1 }));
          }}
          className={`${INLINE_FILTER_FIELD} w-auto min-w-[7.5rem] max-w-[10rem]`}
          aria-label="Invoice type"
        >
          <option value="">All invoice types</option>
          <option value="assessment">Assessment fees</option>
          <option value="patient">Patient fees</option>
        </select>
        <select
          value={filters.payment_mode}
          onChange={(e) => handleFilterChange('payment_mode', e.target.value)}
          className={`${INLINE_FILTER_FIELD} w-auto min-w-[6.5rem] max-w-[9rem]`}
          aria-label="Payment mode"
        >
          <option value="">All modes</option>
          <option value="cash">Cash</option>
          <option value="upi">UPI</option>
          <option value="bank_transfer">Bank Transfer</option>
          <option value="online_link">Online Link</option>
          <option value="card">Card</option>
          <option value="cheque">Cheque</option>
        </select>
        <input
          type="date"
          value={filters.date_from}
          onChange={(e) => handleFilterChange('date_from', e.target.value)}
          className={`${INLINE_FILTER_FIELD} w-[8.75rem]`}
          aria-label="From date"
        />
        <span className="text-xs text-gray-400">–</span>
        <input
          type="date"
          value={filters.date_to}
          onChange={(e) => handleFilterChange('date_to', e.target.value)}
          className={`${INLINE_FILTER_FIELD} w-[8.75rem]`}
          min={filters.date_from || undefined}
          aria-label="To date"
        />
        {hasActiveFilters ? (
          <button
            type="button"
            onClick={clearFilters}
            className="whitespace-nowrap px-1 text-xs font-medium text-gray-500 hover:text-gray-800"
          >
            Clear
          </button>
        ) : null}
        <button
          type="button"
          onClick={() => {
            setShowDeleted((prev) => !prev);
            setFilters((prev) => ({ ...prev, page: 1 }));
          }}
          className={`whitespace-nowrap rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-colors ${
            showDeleted
              ? 'border-red-300 bg-red-50 text-red-700'
              : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
          }`}
          aria-pressed={showDeleted}
        >
          Show deleted
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        <div className="card">
          <p className="text-sm text-gray-500">This Month</p>
          <p className="text-2xl font-bold text-gray-900">
            ₹{(stats?.monthlyReceived || 0).toLocaleString()}
          </p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-500">Total Pending</p>
          <p className="text-2xl font-bold text-amber-600">
            ₹{(stats?.totalPending || 0).toLocaleString()}
          </p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-500">By Payment Mode</p>
          <div className="flex gap-2 mt-2">
            {stats?.byPaymentMode?.map((mode, i) => (
              <span key={i} className="badge bg-gray-100 text-gray-700">
                {mode.payment_mode}: ₹{mode.total?.toLocaleString()}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Payments list */}
      <div className="card">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent"></div>
          </div>
        ) : payments.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <HiOutlineCurrencyRupee className="w-12 h-12 mx-auto text-gray-300 mb-2" />
            <p>{showDeleted ? 'No deleted payments found' : 'No payments found'}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[980px]">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="table-header">Receipt</th>
                  <th className="table-header">Patient</th>
                  <th className="table-header">Invoice</th>
                  <th className="table-header">Invoice Type</th>
                  <th className="table-header">Amount</th>
                  <th className="table-header">Mode</th>
                  <th className="table-header">Date</th>
                  <th className="table-header"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {payments.map((payment) => {
                  const deleted = isRowDeleted(payment);
                  return (
                  <tr
                    key={payment.id}
                    className={`${
                      deleted
                        ? 'bg-red-50 text-red-800 border-l-4 border-red-400'
                        : `hover:bg-gray-50 ${String(payment.id) === String(focusId) ? 'bg-primary-50' : ''}`
                    }`}
                  >
                    <td className="table-cell font-medium">
                      <span className="inline-flex items-center gap-2">
                        {payment.receipt_number}
                        {deleted ? (
                          <span className="badge bg-red-200 text-red-800 text-xs">Deleted</span>
                        ) : null}
                      </span>
                    </td>
                    <td className="table-cell">
                      {payment.patient_name ? (
                        <>
                          <TruncatedName name={payment.patient_name} maxLength={20}>
                            <p className="font-medium" />
                          </TruncatedName>
                          <p className="text-sm text-gray-500">{payment.patient_code}</p>
                        </>
                      ) : payment.lead_name ? (
                        <>
                          <p className="font-medium">Lead: {payment.lead_name}</p>
                          {payment.assessment_date && (
                            <p className="text-sm text-gray-500">Assessment: {formatCalendarDateDMY(payment.assessment_date)}</p>
                          )}
                        </>
                      ) : (
                        <span className="text-gray-400 italic text-sm">Assessment Invoice</span>
                      )}
                    </td>
                    <td className="table-cell">
                      <button
                        onClick={() => handleInvoiceClick(payment.invoice_id)}
                        className="text-primary-600 hover:text-primary-700 hover:underline font-medium"
                        title="View Invoice Details"
                      >
                        {payment.invoice_number}
                      </button>
                    </td>
                    <td className="table-cell">
                      {payment.invoice_type ? (
                        <span className={`badge ${
                          payment.invoice_type === 'assessment' 
                            ? 'bg-blue-100 text-blue-700' 
                            : 'bg-purple-100 text-purple-700'
                        }`}>
                          {payment.invoice_type === 'assessment' ? 'Assessment Fees' : 'Patient Fees'}
                        </span>
                      ) : (
                        <span className="text-gray-400 italic text-sm">-</span>
                      )}
                    </td>
                    <td className="table-cell font-semibold text-green-600">
                      ₹{parseFloat(payment.amount).toLocaleString()}
                    </td>
                    <td className="table-cell capitalize">{payment.payment_mode}</td>
                    <td className="table-cell">
                      <div>{formatDate(payment.payment_date)}</div>
                      {deleted && payment.deleted_at ? (
                        <p className="text-xs text-red-600 mt-0.5">Deleted on {payment.deleted_at}</p>
                      ) : null}
                      {deleted && payment.delete_reason ? (
                        <p className="text-xs text-red-700 mt-0.5" title={payment.delete_reason}>
                          Reason: {payment.delete_reason}
                        </p>
                      ) : null}
                    </td>
                    <td className="table-cell">
                      <div className="flex items-center gap-2">
                        {payment.invoice_id && !deleted ? (
                          <button
                            onClick={() => printInvoicePdf(payment)}
                            className="text-primary-600 hover:text-primary-700 text-sm flex items-center gap-1"
                            title="Print Invoice"
                          >
                            <HiOutlinePrinter className="w-4 h-4" />
                          </button>
                        ) : null}
                        {payment.invoice_id && !deleted ? (
                          <button
                            onClick={() => handleWhatsAppPayment(payment)}
                            className="text-[#128C7E] hover:text-[#075E54] text-sm flex items-center gap-1"
                            title="Send receipt via WhatsApp"
                          >
                            <HiOutlineChatAlt2 className="w-4 h-4" />
                          </button>
                        ) : null}
                        <button
                          onClick={() => handleView(payment)}
                          className="text-primary-600 hover:text-primary-700 text-sm flex items-center gap-1"
                          title="View Details"
                        >
                          <HiEye className="w-4 h-4" />
                        </button>
                        {!deleted ? (
                          <>
                            {canUpdate('payments') && (
                              <button
                                onClick={() => handleEdit(payment)}
                                className="text-green-600 hover:text-green-700 text-sm flex items-center gap-1"
                                title="Edit Payment"
                              >
                                <HiPencil className="w-4 h-4" />
                              </button>
                            )}
                            {canDelete('payments') && (
                              <button
                                onClick={() => handleDelete(payment)}
                                className="text-red-600 hover:text-red-700 text-sm flex items-center gap-1"
                                title="Delete Payment"
                              >
                                <HiTrash className="w-4 h-4" />
                              </button>
                            )}
                          </>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        
        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
              {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
              {pagination.total} results
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
                disabled={pagination.page === 1}
                className="btn btn-secondary text-sm disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
                disabled={pagination.page >= pagination.totalPages}
                className="btn btn-secondary text-sm disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Payment Form Modal */}
      {showForm && (
        <PaymentForm
          payment={selectedPayment}
          invoice={selectedInvoice}
          invoiceType={selectedInvoiceType}
          onClose={() => {
            setShowForm(false);
            setSelectedInvoice(null);
            setSelectedPayment(null);
            setSelectedInvoiceType('');
          }}
          onSuccess={handleFormSuccess}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && paymentToDelete && (
        <DeleteReasonModal
          key={paymentToDelete.id}
          title="Delete Payment"
          message="Are you sure you want to delete this payment?"
          confirmLabel="Delete Payment"
          deleting={deletingPayment}
          onCancel={() => {
            if (deletingPayment) return;
            setShowDeleteConfirm(false);
            setPaymentToDelete(null);
          }}
          onConfirm={confirmDelete}
        >
          <div className="bg-gray-50 p-3 rounded-lg">
            <p className="text-sm text-gray-600">Receipt: <span className="font-medium">{paymentToDelete.receipt_number}</span></p>
            <p className="text-sm text-gray-600">Amount: <span className="font-medium">₹{parseFloat(paymentToDelete.amount).toFixed(2)}</span></p>
            <p className="text-sm text-gray-600">Patient: <span className="font-medium">{paymentToDelete.patient_name || '—'}</span></p>
          </div>
          <p className="text-sm text-amber-600">
            This will also update the invoice balance.
          </p>
        </DeleteReasonModal>
      )}

      {/* Payment Detail Modal */}
      {showDetail && detailPayment && (
        <div className="modal-shell flex items-center justify-center">
          <div className="modal-panel max-w-2xl">
            <div className="p-4 sm:p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <h3 className="text-lg font-semibold">Payment Details</h3>
              <button
                onClick={() => {
                  setShowDetail(false);
                  setDetailPayment(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <HiX className="w-6 h-6" />
              </button>
            </div>
            <div className="p-4 sm:p-6 space-y-4 overflow-y-auto">
              {isRowDeleted(detailPayment) && detailPayment.deleted_at ? (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 space-y-1">
                  <p>This payment was deleted on {detailPayment.deleted_at}.</p>
                  {detailPayment.delete_reason ? (
                    <p><span className="font-medium">Reason:</span> {detailPayment.delete_reason}</p>
                  ) : null}
                </div>
              ) : null}
              {/* Receipt Information */}
              <div className="bg-primary-50 p-4 rounded-lg border border-primary-200">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm text-gray-600">Receipt Number</p>
                    <p className="text-xl font-bold text-primary-700">{detailPayment.receipt_number}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-600">Amount</p>
                    <p className="text-2xl font-bold text-green-600">₹{parseFloat(detailPayment.amount).toFixed(2)}</p>
                  </div>
                </div>
              </div>

              {/* Payment Information */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Payment Date</label>
                  <p className="text-gray-900 font-medium">
                    {formatDate(detailPayment.payment_date)}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Payment Mode</label>
                  <p className="text-gray-900 capitalize font-medium">{detailPayment.payment_mode}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Invoice Number</label>
                  <button
                    onClick={() => handleInvoiceClick(detailPayment.invoice_id)}
                    className="text-primary-600 hover:text-primary-700 hover:underline font-medium"
                    title="View Invoice Details"
                  >
                    {detailPayment.invoice_number}
                  </button>
                </div>
                {detailPayment.received_by_name && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Received By</label>
                    <p className="text-gray-900">{detailPayment.received_by_name}</p>
                  </div>
                )}
              </div>

              {/* Patient Information */}
              <div className="border-t border-gray-200 pt-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Patient Information</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Patient Name</label>
                    <p className="text-gray-900 font-medium">{detailPayment.patient_name}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Patient Code</label>
                    <p className="text-gray-900">{detailPayment.patient_code}</p>
                  </div>
                  {detailPayment.contact_primary && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">Contact</label>
                      <p className="text-gray-900">{detailPayment.contact_primary}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Invoice Information */}
              {detailPayment.invoice_total && (
                <div className="border-t border-gray-200 pt-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Invoice Information</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Invoice Total</label>
                      <p className="text-gray-900 font-medium">₹{parseFloat(detailPayment.invoice_total).toFixed(2)}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Payment Amount</label>
                      <p className="text-green-600 font-semibold">₹{parseFloat(detailPayment.amount).toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Transaction Reference */}
              {detailPayment.transaction_reference && (
                <div className="border-t border-gray-200 pt-4">
                  <label className="text-sm font-medium text-gray-500">Transaction Reference</label>
                  <p className="text-gray-900 font-mono text-sm bg-gray-50 p-2 rounded mt-1">
                    {detailPayment.transaction_reference}
                  </p>
                </div>
              )}

              {/* Notes */}
              {detailPayment.notes && (
                <div className="border-t border-gray-200 pt-4">
                  <label className="text-sm font-medium text-gray-500">Notes</label>
                  <p className="text-gray-900 mt-1 whitespace-pre-wrap">{detailPayment.notes}</p>
                </div>
              )}

              {/* Payment Summary */}
              <div className="border-t border-gray-200 pt-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Payment Status</span>
                    <span className="badge bg-green-100 text-green-700 font-medium">Completed</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="p-4 sm:p-6 border-t border-gray-200 flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3">
              {detailPayment.invoice_id && !isRowDeleted(detailPayment) ? (
                <button
                  type="button"
                  onClick={() => handleWhatsAppPayment(detailPayment)}
                  className="w-full sm:w-auto px-4 py-2.5 bg-[#25D366] text-white rounded-lg hover:bg-[#1da851] flex items-center justify-center gap-2"
                >
                  <HiOutlineChatAlt2 className="w-4 h-4" />
                  Send via WhatsApp
                </button>
              ) : null}
              <button
                onClick={() => {
                  setShowDetail(false);
                  setDetailPayment(null);
                }}
                className="w-full sm:w-auto px-4 py-2.5 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Detail Modal */}
      {showInvoiceDetail && detailInvoice && (
        <div className="modal-shell flex items-center justify-center">
          <div className="modal-panel max-w-4xl">
            <div className="p-4 sm:p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <h3 className="text-lg font-semibold">Invoice Details</h3>
              <button
                onClick={() => {
                  setShowInvoiceDetail(false);
                  setDetailInvoice(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <HiX className="w-6 h-6" />
              </button>
            </div>
            <div className="p-4 sm:p-6 space-y-4 overflow-y-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Invoice Number</label>
                  <p className="text-gray-900 font-medium">{detailInvoice.invoice_number}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Invoice Date</label>
                  <p className="text-gray-900">{formatDate(detailInvoice.invoice_date)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Patient</label>
                  <p className="text-gray-900 font-medium">{detailInvoice.patient_name} ({detailInvoice.patient_code})</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Payment Type</label>
                  <p className="text-gray-900 capitalize">{detailInvoice.payment_type}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500 mb-1 block">Status</label>
                  <span className={`inline-block text-xs px-3 py-1.5 rounded-lg font-medium ${
                    detailInvoice.status === 'paid' ? 'bg-green-100 text-green-700' :
                    detailInvoice.status === 'overdue' ? 'bg-red-100 text-red-700' :
                    detailInvoice.status === 'partial' ? 'bg-amber-100 text-amber-700' :
                    detailInvoice.status === 'sent' ? 'bg-blue-100 text-blue-700' :
                    detailInvoice.status === 'cancelled' ? 'bg-gray-200 text-gray-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {detailInvoice.status.charAt(0).toUpperCase() + detailInvoice.status.slice(1)}
                  </span>
                </div>
                {detailInvoice.due_date && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Due Date</label>
                    <p className="text-gray-900">{formatDate(detailInvoice.due_date)}</p>
                  </div>
                )}
              </div>

              {detailInvoice.items && detailInvoice.items.length > 0 && (
                <div>
                  <label className="text-sm font-medium text-gray-500 mb-2 block">Items</label>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left">Description</th>
                          <th className="px-3 py-2 text-right">Qty</th>
                          <th className="px-3 py-2 text-right">Rate</th>
                          <th className="px-3 py-2 text-right">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {detailInvoice.items.map((item, idx) => (
                          <tr key={idx}>
                            <td className="px-3 py-2">{item.description}</td>
                            <td className="px-3 py-2 text-right">{item.quantity}</td>
                            <td className="px-3 py-2 text-right">₹{parseFloat(item.unit_price).toFixed(2)}</td>
                            <td className="px-3 py-2 text-right">₹{parseFloat(item.amount).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="border-t border-gray-200 pt-4">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Subtotal:</span>
                    <span className="font-medium">₹{parseFloat(detailInvoice.subtotal || 0).toFixed(2)}</span>
                  </div>
                  {detailInvoice.tax_amount > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Tax:</span>
                      <span className="font-medium">₹{parseFloat(detailInvoice.tax_amount).toFixed(2)}</span>
                    </div>
                  )}
                  {detailInvoice.discount_amount > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Discount:</span>
                      <span className="font-medium text-red-600">-₹{parseFloat(detailInvoice.discount_amount).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between pt-2 border-t border-gray-300">
                    <span className="font-semibold">Total Amount:</span>
                    <span className="font-bold text-lg text-primary-600">₹{parseFloat(detailInvoice.total_amount).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Paid Amount:</span>
                    <span className="font-medium text-green-600">₹{parseFloat(detailInvoice.paid_amount || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Balance:</span>
                    <span className="font-medium text-amber-600">₹{parseFloat(detailInvoice.balance_amount).toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {detailInvoice.notes && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Notes</label>
                  <p className="text-gray-900 mt-1">{detailInvoice.notes}</p>
                </div>
              )}

              {detailInvoice.payments && detailInvoice.payments.length > 0 && (
                <div>
                  <label className="text-sm font-medium text-gray-500 mb-2 block">Payment History</label>
                  <div className="space-y-2">
                    {detailInvoice.payments.map((payment, idx) => (
                      <div key={idx} className="p-3 bg-gray-50 rounded-lg text-sm">
                        <div className="flex justify-between">
                          <span className="font-medium">{payment.receipt_number}</span>
                          <span className="font-semibold text-green-600">₹{parseFloat(payment.amount).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                          <span>{formatDate(payment.payment_date)}</span>
                          <span className="capitalize">{payment.payment_mode}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="p-4 sm:p-6 border-t border-gray-200 flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3">
              <button
                onClick={() =>
                  printInvoicePdf({
                    id: detailInvoice?.payments?.[0]?.id,
                    invoice_id: detailInvoice.id,
                    invoice_number: detailInvoice.invoice_number,
                    template_code: detailInvoice.template_code,
                    payment_mode: detailInvoice?.payments?.[0]?.payment_mode || null,
                    payment_date: detailInvoice?.payments?.[0]?.payment_date || null,
                    receipt_number: detailInvoice?.payments?.[0]?.receipt_number || null,
                  })
                }
                className="w-full sm:w-auto px-4 py-2.5 border border-primary-200 text-primary-700 bg-primary-50 rounded-lg hover:bg-primary-100 flex items-center justify-center gap-2"
              >
                <HiOutlinePrinter className="w-4 h-4" />
                Print Invoice
              </button>
              <button
                onClick={() =>
                  downloadInvoicePdf({
                    id: detailInvoice?.payments?.[0]?.id,
                    invoice_id: detailInvoice.id,
                    invoice_number: detailInvoice.invoice_number,
                    template_code: detailInvoice.template_code,
                    payment_mode: detailInvoice?.payments?.[0]?.payment_mode || null,
                    payment_date: detailInvoice?.payments?.[0]?.payment_date || null,
                    receipt_number: detailInvoice?.payments?.[0]?.receipt_number || null,
                  })
                }
                className="w-full sm:w-auto px-4 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center justify-center gap-2"
              >
                <HiOutlineDownload className="w-4 h-4" />
                Download
              </button>
              <button
                onClick={() => {
                  setShowInvoiceDetail(false);
                  setDetailInvoice(null);
                }}
                className="w-full sm:w-auto px-4 py-2.5 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <SendWhatsAppModal
        open={showWhatsAppModal && !!whatsappPayment}
        onClose={() => {
          setShowWhatsAppModal(false);
          setWhatsappPayment(null);
        }}
        title="Send payment receipt via WhatsApp"
        documentLabel="Payment receipt"
        onPreview={previewPaymentWhatsApp}
        onSend={sendPaymentWhatsApp}
      />

    </div>
  );
};

export default Payments;

